import React, { ReactNode, Ref, forwardRef, useCallback, useMemo } from 'react';
import { Link as LinkOriginal, LinkProps as LinkPropsOriginal } from 'react-router-dom';
import { universalRenderer } from 'utils/universalRenderer';
import { classNames } from 'primereact/utils';
import { Icon, IconSet, IconStyle } from '../Icon';
import './Link.scss';

type LinkVariantResponsive = 'sm' | 'md';

type LinkVariant = 'standard' | 'purple' | 'grey' | 'danger' | 'success';

export type LinkProps = LinkPropsOriginal & {
	icon?: string | ReactNode;
	iconSet?: IconSet;
	iconStyle?: IconStyle;
	iconClassName?: string;
	iconPosition?: 'left' | 'right';
	iconOnly?: boolean;
	variant?: LinkVariant;
	label?: string;
	noUnderline?: boolean;
	disabled?: boolean;
	responsive?: LinkVariantResponsive;
	contentClassName?: string;
	scrollToTop?: boolean;
	preventDefaultOnClick?: boolean;
};

export type LinkExtendProps = LinkPropsOriginal;

export const Link = forwardRef(
	(
		{
			icon,
			iconSet,
			iconStyle,
			iconClassName,
			iconPosition = 'left',
			iconOnly = false,
			variant = 'standard',
			label,
			children,
			className,
			contentClassName,
			noUnderline = false,
			disabled = false,
			responsive,
			scrollToTop = false,
			preventDefaultOnClick = false,
			...restProps
		}: LinkProps,
		ref: Ref<HTMLAnchorElement>
	) => {
		const renderIcon = () => {
			if (typeof icon === 'string') {
				return (
					<span
						className={classNames('link-icon-container', {
							'link-icon-right mr-1': !iconOnly && iconPosition !== 'left',
							'link-icon-left ml-1': !iconOnly && iconPosition !== 'right',
						})}
					>
						<Icon
							name={icon}
							iconSet={iconSet}
							iconStyle={iconStyle}
							className={classNames('link-icon', iconClassName, {
								// 'link-icon-left mr-1': !iconOnly && iconPosition !== 'left',
								// 'link-icon-right ml-1': !iconOnly && iconPosition !== 'right',
							})}
						/>
					</span>
				);
			}
			return <span className={classNames('link-icon', iconClassName)}>{universalRenderer(icon)}</span>;
		};

		const finalClassName = useMemo(
			() =>
				classNames(
					'link',
					{ 'link-underline': !noUnderline, 'link-disabled': disabled, 'link-icon-only': iconOnly },
					`link-variant-${variant}`,
					className
				),
			[noUnderline, disabled, variant, iconOnly, className]
		);

		const handleClick: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
			(event) => {
				if (preventDefaultOnClick) {
					event.preventDefault();
				}
				if (scrollToTop) {
					document.body.scrollTop = 0;
					document.getElementById('scrollWrapperRef')?.scrollTo({ top: 0 });
					document.getElementById('scrollWrapperInnerRef')?.scrollTo({ top: 0 });
					document.getElementById('pageScrollWrapperRef')?.scrollTo({ top: 0 });
					document.getElementById('knowledgeBaseContainer')?.scrollTo({ top: 0 });
				}
				setTimeout(() => {
					restProps?.onClick?.(event);
				}, 100);
			},
			[restProps, scrollToTop]
		);

		return (
			<LinkOriginal ref={ref} className={finalClassName} aria-label={label} {...restProps} onClick={handleClick}>
				{icon && iconPosition === 'left' && renderIcon()}
				{!iconOnly && (
					<span
						className={classNames('link-text', {
							[`hidden ${responsive}:${contentClassName || 'inline'}`]: Boolean(responsive),
							[contentClassName || 'inline']: !responsive,
						})}
					>
						{children}
					</span>
				)}
				{icon && iconPosition === 'right' && renderIcon()}
			</LinkOriginal>
		);
	}
);
